// Silent access-token refresh.
//
// auth-service issues a 7-day refresh token as an httpOnly cookie at login and
// exposes POST /api/v1/auth/refresh, which reads that cookie and returns a fresh
// access token (rotating the cookie). The SPA never sees the refresh token.
//
// This module is the single source of truth for refreshing. It is framework-
// agnostic (localStorage only) and single-flight so a burst of 401s triggers
// exactly one refresh.
import axios from 'axios';
import { setCsrf } from './session';

const API_BASE = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Bare client (no interceptors) so refreshing can't recurse into itself.
const bare = axios.create({ baseURL: API_BASE, withCredentials: true, timeout: 15000 });

let inFlight: Promise<boolean> | null = null;

/**
 * P2-8: rotate the session via the httpOnly refresh cookie. The backend rotates
 * BOTH cookies (access + refresh) and never returns the token to JS, so we no
 * longer store an access token — we just refresh the session-bound CSRF token.
 * Returns true on success, false on a dead/expired session. Concurrent callers
 * share a single network request (single-flight).
 */
export function refreshAccessToken(): Promise<boolean> {
  if (inFlight) return inFlight;

  inFlight = bare
    .post('/api/v1/auth/refresh')
    .then(async () => {
      // Cookie rotated by the backend → re-capture the CSRF token bound to it.
      try {
        const s = await bare.get('/api/v1/auth/session');
        setCsrf(s.data?.csrfToken ?? null);
      } catch {
        /* non-fatal — writes will 403 until the next successful /session */
      }
      sessionStorage.removeItem('hr_session_expired');
      return true;
    })
    .catch(() => false)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}
